let elementName = `nss-buybox-sticky-atc-variant`;
if (typeof customElements.get(elementName) === `undefined`) {

  class NSSBuyboxStickyATC extends HTMLElement {

    constructor() {
      super();

      this.debug = false;
    }

    init() {

      this.els = {
        qtyItems: this.querySelectorAll(`[nss-sticky-qty-item-count]`),
        frequencyItems: this.querySelectorAll(`[nss-frequency-update]`),
        atcBtn: this.querySelector(`[nss-add-to-cart-btn]`),
        planSelect: this.querySelector(`[nss-sticky-plan-select]`),
        planName: this.querySelector(`[nss-sticky-plan-name]`),
        perServing: this.querySelector(`[nss-sticky-per-serving]`),
        btnPrice: this.querySelector(`[nss-sticky-current-price]`),
        btnCompare: this.querySelector(`[nss-sticky-compare-at-price]`),
      };

      this.id = this.getAttribute(`nss-variant-id`);

      // Cache every subscription <option> (tagged with its selling plan group
      // id). The select is rebuilt to show OTP + only the active quantity
      // tier's group options.
      this.subOptions = this.els.planSelect
        ? Array.prototype.filter.call(this.els.planSelect.options, (o) =>
            o.hasAttribute(`data-spg-id`),
          )
        : [];
    }

    // Selling plan group id for the currently selected quantity tier.
    // Falls back to the Liquid-computed default, then the first group found.
    getActiveGroupId() {
      const checked = document.querySelector(`input[name="quantity"]:checked`);
      const fromQty = checked ? checked.getAttribute(`data-spg-id`) : ``;
      if (fromQty) return fromQty;
      const fromDefault = this.getAttribute(`data-default-spg-id`);
      if (fromDefault) return fromDefault;
      return this.subOptions.length ? this.subOptions[0].getAttribute(`data-spg-id`) : ``;
    }

    // Keep only OTP + the active group's plan options in the select.
    rebuildPlanOptions() {
      const sel = this.els.planSelect;
      if (!sel || !this.subOptions.length) return;

      const gid = this.getActiveGroupId();
      let active = this.subOptions.filter((o) => o.getAttribute(`data-spg-id`) === gid);
      if (!active.length) {
        // Unknown group for this quantity — fall back to the first group
        const firstGid = this.subOptions[0].getAttribute(`data-spg-id`);
        active = this.subOptions.filter((o) => o.getAttribute(`data-spg-id`) === firstGid);
      }

      const previous = sel.value;

      // Remove all sub options currently in the select, then append the
      // active group's (cached nodes are moved, not cloned — no listener loss)
      Array.prototype.slice.call(sel.options).forEach((o) => {
        if (o.hasAttribute(`data-spg-id`)) o.remove();
      });
      active.forEach((o) => sel.appendChild(o));

      // Preserve intent: if a subscription was selected but its plan belongs
      // to another tier's group, select the new group's first plan; OTP stays.
      if (previous !== ``) {
        const stillThere = Array.prototype.some.call(sel.options, (o) => o.value === previous);
        sel.value = stillThere ? previous : (active[0] ? active[0].value : ``);
      }
    }

    // Reflect the selected option in the pill display + Add button price.
    // The Add button total = per-unit plan price * the quantity chosen in the
    // quantity selector (input[name="quantity"]), matching the main buy button's
    // basePrice * qty calculation in NSSBuyButtonsBlock.
    syncPlanDisplay() {
      const sel = this.els.planSelect;
      if (!sel) return;
      const opt = sel.options[sel.selectedIndex];
      if (!opt) return;
      if (this.els.planName) this.els.planName.textContent = opt.textContent.trim();

      const qty = this.getQuantity();
      const priceCents = parseInt(opt.getAttribute(`data-price`), 10);
      const compareCents = parseInt(opt.getAttribute(`data-compare-at-price`), 10);
      // data-compare-money is only rendered when there's a genuine compare price.
      const hasCompare = !!(opt.getAttribute(`data-compare-money`) || ``).trim();

      // OTP option value is "" (empty); any non-empty value is a subscription plan.
      const isSub = !!(sel.value && sel.value.trim());

      if (this.els.btnPrice) {
        let total = null;
        if (isSub) {
          // Subscription: priceCents is the selected frequency's per-delivery
          // price from the active quantity tier's own selling plan group — the
          // group already carries the tier's pricing, so NO extra discount.
          if (!isNaN(priceCents)) total = this.lineTotal(priceCents, qty, 0);
        } else {
          // One-time: prefer the discount-inclusive line total from the radio,
          // else per-unit * qty.
          const stored = this.getQtyTotal(false);
          total = stored != null ? stored : (isNaN(priceCents) ? null : priceCents * qty);
        }
        this.els.btnPrice.textContent = total != null
          ? this.formatMoney(total)
          : (opt.getAttribute(`data-price-money`) || ``);
      }
      if (this.els.btnCompare) {
        this.els.btnCompare.textContent =
          hasCompare && !isNaN(compareCents) ? this.formatMoney(compareCents * qty) : ``;
      }
      this.updatePerServing();
    }

    // Quantity chosen in the quantity selector radio group. Defaults to 1.
    getQuantity() {
      const checked = document.querySelector(`input[name="quantity"]:checked`);
      let qty = checked ? parseInt(checked.value, 10) : 1;
      if (!qty || qty < 1) qty = 1;
      return qty;
    }

    // Discount-inclusive line total stored on the checked quantity radio.
    // Returns null when absent (e.g. fallback radios) so callers compute
    // per-unit * qty instead.
    getQtyTotal(isSub) {
      const checked = document.querySelector(`input[name="quantity"]:checked`);
      if (!checked) return null;
      const stored = checked.getAttribute(isSub ? `data-sub-price` : `data-otp-price`);
      const cents = parseInt(stored, 10);
      if (stored == null || stored === `` || isNaN(cents) || cents <= 0) return null;
      return cents;
    }

    // OTP display discount (%) from the checked quantity radio — mirrors the
    // admin automatic discount. 0 when absent. Never applied to subscriptions.
    getOtpDiscount() {
      const checked = document.querySelector(`input[name="quantity"]:checked`);
      if (!checked) return 0;
      const d = parseInt(checked.getAttribute(`data-otp-discount`), 10);
      return isNaN(d) ? 0 : d;
    }

    // Apply a percentage discount to a line total the way Shopify's cart does:
    // round the discount on the line subtotal, then subtract.
    lineTotal(unit, qty, discountPct) {
      const subtotal = unit * qty;
      if (discountPct > 0) return subtotal - Math.round((subtotal * discountPct) / 100);
      return subtotal;
    }

    // Format cents using the same money format as the main buy button so the
    // sticky price matches exactly.
    formatMoney(cents) {
      const priceEl = document.querySelector(`.nss-product-form__submit-price`);
      const moneyFormat = priceEl ? priceEl.dataset.moneyFormat : null;
      if (moneyFormat && window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents, moneyFormat);
      }
      const symbol = this.getAttribute(`data-currency-symbol`) || `$`;
      const dollars = Math.floor(cents / 100);
      let c = cents % 100;
      c = c < 10 ? `0${c}` : `${c}`;
      return `${symbol}${dollars}.${c}`;
    }

    // Per-serving. One-Time: variant price with the OTP display discount
    // applied (mirrors the quantity selector's per-day). Subscription: the
    // selected plan's per-delivery price straight from the active tier's
    // group — quantity cancels out, NO extra discount.
    updatePerServing() {
      if (!this.els.perServing) return;
      const otpPrice = parseInt(this.getAttribute(`data-variant-price`), 10);
      const supplyCount = parseInt(this.getAttribute(`data-supply-count`), 10);
      const symbol = this.getAttribute(`data-currency-symbol`) || ``;
      if (!otpPrice || !supplyCount) return;

      const sel = this.els.planSelect;
      const isSub = !!(sel && sel.value && sel.value.trim());

      let unit;
      if (isSub) {
        const opt = sel.options[sel.selectedIndex];
        unit = opt ? parseInt(opt.getAttribute(`data-price`), 10) : NaN;
        if (isNaN(unit) || !unit) return;
      } else {
        const disc = this.getOtpDiscount();
        // Shopify floors the per-unit discount amount to the cent — match it
        // so the per-serving derives from the same unit price the cart charges.
        unit = disc > 0 ? otpPrice - Math.floor((otpPrice * disc) / 100) : otpPrice;
      }

      this.els.perServing.textContent = this.formatPerServing(unit / supplyCount, symbol);
    }

    formatPerServing(cents, symbol) {
      cents = Math.round(cents);
      const dollars = Math.floor(cents / 100);
      let c = cents % 100;
      c = c < 10 ? `0${c}` : `${c}`;
      return `${symbol}${dollars}.${c}/Serving`;
    }

    // Point this sticky's select at a plan id ("" = One-Time Purchase) without
    // re-broadcasting — used when reflecting a change from the main buybox.
    reflectPlanId(planId) {
      const sel = this.els.planSelect;
      if (!sel) return;
      const value = planId == null ? `` : String(planId);
      let hasOption = Array.prototype.some.call(sel.options, (o) => o.value === value);
      if (!hasOption && value !== ``) {
        // The plan may belong to a tier group not currently in the select
        // (e.g. buybox switched groups before we rebuilt) — rebuild and retry.
        this.rebuildPlanOptions();
        hasOption = Array.prototype.some.call(sel.options, (o) => o.value === value);
      }
      if (!hasOption || sel.value === value) {
        if (hasOption) this.syncPlanDisplay();
        return;
      }
      sel.value = value;
      this.syncPlanDisplay();
    }

    connectedCallback() {
      if (this.debug) console.log(`[NSS][Buybox Sticky ATC] Connected.`);

      this.init();

      this.initStickyObservers();

      // Delegated change handler for the plan select inside
      // .nss-buybox-sticky-atc__plan-select-wrap. Bound to the element (change
      // events from <select> bubble) so it keeps working even if the inner
      // markup is re-rendered, and always re-syncs the Add button price.
      this.addEventListener(`change`, (e) => {
        const sel = e.target && e.target.closest && e.target.closest(`[nss-sticky-plan-select]`);
        if (!sel) return;
        this.els.planSelect = sel;
        this.syncPlanDisplay();
        // Broadcast to the main buybox so it adds the matching plan/OTP
        document.dispatchEvent(new CustomEvent(`nss:setsellingplan`, {
          detail: { planId: sel.value },
        }));
      });
      if (this.els.planSelect) {
        this.rebuildPlanOptions();
        this.syncPlanDisplay();
      }

      // Reflect changes made in the main buybox selling-plan selector
      this._onBuyboxPlanChange = (e) => {
        if (e.detail && e.detail.planId != null) this.reflectPlanId(e.detail.planId);
        this.updatePerServing();
      };
      document.addEventListener(`nss:sellingplanchange`, this._onBuyboxPlanChange);

      // Also recompute per-serving directly from the buybox radios / frequency
      // select (robust even if the buybox event isn't dispatched)
      this._onSellingPlanUiChange = (e) => {
        const t = e.target;
        if (t && t.classList && (t.classList.contains(`nss-selling-plan__radio`) ||
            t.classList.contains(`nss-selling-plan__frequency-select`))) {
          this.updatePerServing();
        }
      };
      document.addEventListener(`change`, this._onSellingPlanUiChange);

      // Quantity change: swap the plan options to the new tier's group, then
      // re-sync the Add button price / per-serving.
      this._onQuantityChange = (e) => {
        if (e.target && e.target.name === `quantity`) {
          this.rebuildPlanOptions();
          this.syncPlanDisplay();
        }
      };
      document.addEventListener(`change`, this._onQuantityChange);

      this.addEventListener(`click`, this.handleClick.bind(this));
      this.addEventListener(`buyboxUpdate`, this.handleUpdate.bind(this));
      // this.addEventListener(`buyboxReload`, this.reloadHandler.bind(this));
    }

    disconnectedCallback() {
      if (this._onBuyboxPlanChange) {
        document.removeEventListener(`nss:sellingplanchange`, this._onBuyboxPlanChange);
      }
      if (this._onSellingPlanUiChange) {
        document.removeEventListener(`change`, this._onSellingPlanUiChange);
      }
      if (this._onQuantityChange) {
        document.removeEventListener(`change`, this._onQuantityChange);
      }
    }

    async fetchSection() {
      const parser = new DOMParser();
      const sectionID = this.getAttribute(`nss-parent-section-id`);
      const markup = await (await fetch(`?section_id=${sectionID}`)).text();
      const dom = parser.parseFromString(markup, "text/html");
      return dom;
    }

    async reloadHandler() {
      const dom = await this.fetchSection();

      const selectorParent = `nss-buybox-sticky-atc-variant[nss-variant-id="${this.id}"]`;
      const selectorChild = `.nss-buybox-sticky-atc__inner`;
      const selector = `${selectorParent} ${selectorChild}`;
      const newAtc = dom.querySelector(selector);
      const thisAtc = this.querySelector(selectorChild);
      thisAtc.replaceWith(newAtc);

      this.init();
    }

    handleClick(e) {
      if (this.debug) console.log(`[NSS][Buybox Sticky ATC] Click - e.target:`, e.target);

      // closest() so clicks on the button's inner label spans still register
      const atcBtn = e.target.closest(`[nss-add-to-cart-btn]`);
      if (!atcBtn) return;

      // The real add-to-cart form lives in the nss-buy-buttons block
      const formEl =
        document.querySelector(`product-form.nss-product-form form`) ||
        document.querySelector(`form[id^="nss-product-form-"]`);

      if (!formEl) {
        if (this.debug) console.warn(`[NSS][Buybox Sticky ATC] No product form found`);
        return;
      }

      const submitter = formEl.querySelector(`[name="add"]`);
      if (typeof formEl.requestSubmit === `function`) {
        formEl.requestSubmit(submitter || undefined);
      } else if (submitter) {
        submitter.click();
      } else {
        formEl.submit();
      }
    }

    initStickyObservers() {
      const controlsEls = document.querySelectorAll(`nss-buybox-summary`);
      const footerEl = document.querySelector(`nss-footer`);

      const observerOptions = {
        root: null,
        threshold: 0.1,
      };

      // defaults
      this.controlsInView = true;
      this.footerInView = false;

      const observerCallback = (entries) => {
        entries.forEach((entry) => {
          if (entry.target === footerEl) {
            this.footerInView = entry.isIntersecting;
          } else {
            this.controlsInView = entry.isIntersecting;
          }

          if (!this.controlsInView && !this.footerInView) {
            this.showStickyBar();
          } else {
            this.hideStickyBar();
          }
        });
      };

      const observer = new IntersectionObserver(
        observerCallback,
        observerOptions
      );

      controlsEls.forEach((controlsEl) => {
        observer.observe(controlsEl);
      });
      observer.observe(footerEl);
    }

    showStickyBar() {
      this.classList.add(`nss-open`);
      // this.els.docEl.style.setProperty(`--nss-sticky-atc-pos`, `100px`);
    }

    hideStickyBar() {
      this.classList.remove(`nss-open`);
      // this.els.docEl.style.setProperty(`--nss-sticky-atc-pos`, `0px`);
    }

    handleUpdate(e) {
      const oldId = e.detail.oldId;
      const newId = e.detail.newId;

      if (newId === this.id) {
        if (this.debug) console.log(`[NSS][Buybox Sticky ATC] handleUpdate e.detail:`, e.detail);
        this.classList.add(`nss-active`);
      } else {
        this.classList.remove(`nss-active`);
      }

      if (newId === this.id && oldId === newId) {
        // Update quantity
        if (this.debug) console.log(`[NSS][Buybox Sticky ATC] handleUpdate e.detail.quantity:`, e.detail.quantity);
        if (e.detail.quantity !== null) {
          this.els.qtyItems.forEach((qtyItemEl) => {
            const count = qtyItemEl.getAttribute(`nss-sticky-qty-item-count`);
            const total = count * parseInt(e.detail.quantity);
            qtyItemEl.innerHTML = total;
          });
        }

        // Update selling plan
        if (this.debug) console.log(`[NSS][Buybox Sticky ATC] handleUpdate e.detail.sellingPlanId:`, e.detail.sellingPlanId);
        if (e.detail.sellingPlanId !== null) {
          this.els.frequencyItems.forEach((frequencyItemEl) => {
            frequencyItemEl.classList.remove(`nss-active`);
          });
          const updateFrequencyEls = this.querySelectorAll(`[nss-frequency-update="${e.detail.sellingPlanId}"]`);
          updateFrequencyEls.forEach((updateFrequencyEl) => {
            updateFrequencyEl.classList.add(`nss-active`);
          });
        }

        // Update ATC text
        if (this.els.atcBtn) {
          const atcSpanEl = this.els.atcBtn.querySelector(`span`);
          // let atcText = "ADD TO CART";
          // if (e.detail.sellingPlanId !== "") {
          //   atcText = this.els.atcBtn.getAttribute(`nss-atc-sub-text`);
          // } else {
          //   atcText = this.els.atcBtn.getAttribute(`nss-atc-otp-text`);
          // }
          // atcSpanEl.innerHTML = atcText;
        }
      }
    }
  }

  customElements.define(elementName, NSSBuyboxStickyATC);
} else {
  console.warn(`[NSS] Element '${elementName}' already defined.`);
}